import formHtml from './form.html';
import { getFormSnapshot, logFormEvent, sendToMakeWebhook, sendToSrf } from './api';
import { showCustomConfirm } from './confirm';
import { getMinDateISO } from './dates';
import { byId, inputById, inputValue } from './dom';
import { clearInlineError, showInlineError } from './errors';
import { collectMarketingData } from './marketing';
import {
  addMultiTrip,
  closeMultiModal,
  initMultiTrips,
  openMultiModal,
  openMultiModalAsView,
  resetMultiTripsToEmpty,
  showEditView,
  updateSummaryView,
} from './multitrip';
import { AUTOCOMPLETE_OPTIONS, extractCity, extractCountry, isPlaceSpecificEnough } from './places';
import { autocompleteState, fieldState, multiState, placeData, touchAllFields } from './state';
import type { AutocompleteFieldId, StepperName, TripType } from './types';
import { clamp } from './utils';
import { extractFormDataForAnalytics, FORM_LANDING_VARIANT, serializeForm } from './serialize';
import { updateReturnFieldsState, validateForm } from './validation';

// --- STEPPERY (pasażerowie / bagaże / foteliki) ---

const STEPPER_LIMITS: Record<StepperName, { min: number; max: number }> = {
  passengers: { min: 5, max: 9999 },
  bags: { min: 0, max: 9999 },
  seats: { min: 0, max: 9999 },
};

function updateStepperButtons(): void {
  (Object.keys(STEPPER_LIMITS) as StepperName[]).forEach((name) => {
    const input = inputById(name);
    if (!input) return;
    const current = parseInt(input.value, 10) || 0;
    const minusBtn = document.querySelector<HTMLButtonElement>(`button[data-step="${name}"][data-dir="-1"]`);
    const plusBtn = document.querySelector<HTMLButtonElement>(`button[data-step="${name}"][data-dir="1"]`);
    if (minusBtn) minusBtn.disabled = current <= STEPPER_LIMITS[name].min;
    if (plusBtn) plusBtn.disabled = current >= STEPPER_LIMITS[name].max;
  });
}

function changeStep(name: StepperName, dir: number): void {
  const input = inputById(name);
  if (!input) return;
  const current = parseInt(input.value, 10) || 0;
  input.value = String(clamp(current + dir, STEPPER_LIMITS[name]));
  updateStepperButtons();
  validateForm();
}

// --- TRIP TYPE / TABS ---

function setTripType(type: TripType): void {
  const isReturn = type === 'return';
  const isMulti = type === 'multitrip';
  const tripTypeInput = inputById('tripType');
  if (tripTypeInput) tripTypeInput.value = type;

  byId('oneWayBtn')?.setAttribute('aria-pressed', String(type === 'oneway'));
  byId('returnBtn')?.setAttribute('aria-pressed', String(isReturn));
  byId('multiBtn')?.setAttribute('aria-pressed', String(isMulti));

  document.querySelectorAll('.return-group').forEach((el) => el.classList.toggle('hidden', !isReturn || isMulti));
  document.querySelectorAll('.standard-field').forEach((el) => el.classList.toggle('hidden', isMulti));
  byId('multiTripGroup')?.classList.toggle('hidden', !isMulti);

  (['returnPickup', 'returnDestination', 'returnDate', 'returnTime'] as const).forEach((id) => {
    const el = inputById(id);
    if (el) el.required = isReturn && !isMulti;
  });

  (['pickup', 'destination', 'pickupDate', 'pickupTime'] as const).forEach((id) => {
    const el = inputById(id);
    if (el) el.required = !isMulti;
  });

  updateSummaryView();
  validateForm();
}

function handleTabSwitch(newType: TripType): void {
  const currentType = inputValue('tripType');
  const targetLabel = newType === 'oneway' ? 'One way' : 'Return trip';

  // Zmiana z Multi-trip na inny tryb kasuje wypełnione segmenty — pytamy o potwierdzenie.
  if (currentType === 'multitrip' && newType !== 'multitrip' && multiState.trips.some((t) => t.pickup || t.destination)) {
    showCustomConfirm(
      `Switch to ${targetLabel}?`,
      `Switching to ${targetLabel} will clear all your multi-trip segments - you'll be starting fresh. This can't be undone.`,
      'Keep my trips',
      `Yes, switch to ${targetLabel}`,
      () => {
        resetMultiTripsToEmpty();
        setTripType(newType);
      },
    );
  } else {
    setTripType(newType);
  }
}

// --- AUTOCOMPLETE (pola standardowe) ---

function initAutocomplete(): void {
  const inputIds: AutocompleteFieldId[] = ['pickup', 'destination', 'returnPickup', 'returnDestination'];

  inputIds.forEach((id) => {
    const inputElement = inputById(id);
    if (!inputElement || !window.google) return;

    const autocomplete = new google.maps.places.Autocomplete(inputElement, AUTOCOMPLETE_OPTIONS);
    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      clearInlineError(id);

      if (!place.geometry) {
        autocompleteState[id] = false;
        placeData[id] = null;
        showInlineError(id, 'Please select the full street address from the dropdown.');
        validateForm();
        return;
      }
      if (!isPlaceSpecificEnough(place)) {
        autocompleteState[id] = false;
        placeData[id] = null;
        showInlineError(id, 'Please select the full street address from the dropdown.');
        if (fieldState[id]) fieldState[id].touched = true;
        validateForm();
        return;
      }

      autocompleteState[id] = true;
      placeData[id] = {
        coordinates: {
          latitude: place.geometry.location?.lat() ?? 0,
          longitude: place.geometry.location?.lng() ?? 0,
        },
        country: extractCountry(place.address_components),
        city: extractCity(place.address_components),
        fullAddress: place.formatted_address ?? '',
      };
      validateForm();
    });

    inputElement.addEventListener('input', () => {
      autocompleteState[id] = false;
      placeData[id] = null;
      clearInlineError(id);
    });
  });
}

// --- KOMUNIKATY BŁĘDÓW ---

function showError(msg: string): void {
  let errEl = byId('errMsg');
  if (!errEl) {
    errEl = document.createElement('div');
    errEl.id = 'errMsg';
    errEl.className = 'msg err';
    document.querySelector('.actions')?.prepend(errEl);
  }
  errEl.textContent = msg;
  errEl.style.display = 'block';
  validateForm();
}

// --- SUBMIT ---

async function handleSubmit(e: SubmitEvent): Promise<void> {
  e.preventDefault();

  touchAllFields();
  if (!validateForm()) return;

  void logFormEvent('submit_attempt', 'Użytkownik próbuje wysłać formularz', { data: getFormSnapshot() });

  const submitBtn = byId<HTMLButtonElement>('submitBtn');
  if (submitBtn) {
    submitBtn.disabled = true;
    submitBtn.classList.remove('primary');
  }

  const isMulti = inputValue('tripType') === 'multitrip';
  try {
    const payload = serializeForm();

    try {
      const formData = extractFormDataForAnalytics(payload);
      const messageData = { event: 'form_submit', ...formData };
      const parentOrigin = document.referrer ? new URL(document.referrer).origin : '*';
      window.parent.postMessage(messageData, parentOrigin);
      await new Promise((resolve) => setTimeout(resolve, 300));
    } catch (err) {
      console.error('❌ PostMessage error:', err);
    }

    // Routing wysyłki: multitrip idzie na wcześniejszy webhook Make (/wyslij-multi),
    // a single i return na nowy endpoint SRF.
    await (isMulti ? sendToMakeWebhook(payload) : sendToSrf(payload));
    void logFormEvent('submit_success', 'Webhook wysłany poprawnie', { data: getFormSnapshot() });

    const submissionId = payload[0]?.customer_details?.submission_id;

    const countryData = window.iti?.getSelectedCountryData() ?? null;
    const countryOfOrigin = countryData ? countryData.name.replace(/\s*\(.*?\)/, '') : '';
    let countryOfService = placeData.pickup?.country ?? '';
    if (isMulti && multiState.trips.length > 0 && multiState.trips[0].pickupCountry) {
      countryOfService = multiState.trips[0].pickupCountry;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'special-request',
      pickup_city: inputValue('pickup').trim(),
      destination_city: inputValue('destination').trim(),
      pickup_date: inputValue('pickupDate'),
      pickup_time: inputValue('pickupTime'),
      'DLV - Passenger Count': Number(inputValue('passengers')) || 0,
      luggage_count: Number(inputValue('bags')) || 0,
      seats_count: Number(inputValue('seats')) || 0,
      transfer_type: inputValue('tripType'),
      full_name: inputValue('fullName').trim(),
      email: inputValue('email').trim(),
      telephone: window.iti?.getNumber() ?? '',
      submission_id: submissionId,
      form_id: 'tripForm',
      ab_variant: payload[0]?.customer_details?.ab_variant,
      ab_landing_variant: FORM_LANDING_VARIANT,
      country_of_origin: countryOfOrigin,
      country_of_service: countryOfService,
    });

    if (isMulti) {
      // Reset stanu multi-trip, żeby nie dało się wysłać tego samego dwa razy
      resetMultiTripsToEmpty();
      byId('multiModalOverlay')?.classList.add('hidden');
      updateSummaryView();

      showCustomConfirm(
        "You're all set!",
        'Your booking request has been received. Our team will review your itinerary and get back to you shortly with a tailored quote.',
        '',
        'Got it',
        null,
        'celebration',
      );
    } else if (submissionId) {
      await new Promise((resolve) => setTimeout(resolve, 200));
      window.location.href = `/proposal-view/?submission_id=${submissionId}`;
    } else {
      showError('Wystąpił nieoczekiwany błąd. Prosimy spróbować ponownie.');
    }
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    void logFormEvent('submit_error', 'Błąd podczas wysyłki formularza', { error: message, data: getFormSnapshot() });
    showError(`The form could not be sent. (${message})`);
  }
}

// --- INICJALIZACJA I EVENT LISTENERY ---

function initForm(): void {
  const form = byId<HTMLFormElement>('tripForm');
  if (!form) return;
  let formStartFired = false;

  collectMarketingData();

  form.querySelectorAll<HTMLElement>('input[type="text"], input[type="email"], textarea').forEach((input) => {
    input.addEventListener('input', () => {
      if (!formStartFired) {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'form_start' });
        void logFormEvent('form_start', 'Użytkownik rozpoczął wypełnianie formularza');
        formStartFired = true;
      }
    });
  });

  inputById('returnTime')?.addEventListener('input', () => {
    fieldState.returnTime.touched = true;
    validateForm();
  });

  updateReturnFieldsState();

  inputById('pickupDate')?.addEventListener('change', () => {
    updateReturnFieldsState();
    validateForm();
  });

  inputById('pickupTime')?.addEventListener('change', () => {
    updateReturnFieldsState();
    validateForm();
  });

  // Ogranicz część roku w datach do 4 cyfr podczas wpisywania.
  (['pickupDate', 'returnDate'] as const).forEach((id) => {
    inputById(id)?.addEventListener('input', (e) => {
      const target = e.target as HTMLInputElement;
      const parts = target.value.split('-');
      if (parts[0] && parts[0].length > 4) {
        parts[0] = parts[0].slice(0, 4);
        target.value = parts.join('-');
      }
    });
  });

  const pickupDate = inputById('pickupDate');
  const returnDate = inputById('returnDate');
  const minDateISO = getMinDateISO();

  if (pickupDate) {
    pickupDate.min = minDateISO;
    pickupDate.addEventListener('change', () => {
      if (!returnDate) return;
      if (pickupDate.value) {
        returnDate.min = pickupDate.value;
        if (returnDate.value && returnDate.value < pickupDate.value) returnDate.value = '';
      } else {
        returnDate.min = minDateISO;
      }
    });
  }

  form.querySelectorAll<HTMLElement>('input, select, textarea').forEach((input) => {
    const id = input.id;
    if (fieldState[id]) {
      input.addEventListener('blur', () => {
        fieldState[id].touched = true;
        validateForm();
      });
    }
  });

  initAutocomplete();

  (['pickupDate', 'pickupTime', 'returnDate', 'returnTime'] as const).forEach((id) => {
    inputById(id)?.addEventListener('input', () => {
      if (fieldState[id]) fieldState[id].touched = true;
      validateForm();
    });
  });

  const phoneInput = inputById('phone');
  if (phoneInput && window.intlTelInput) {
    window.iti = window.intlTelInput(phoneInput, {
      utilsScript: 'https://cdnjs.cloudflare.com/ajax/libs/intl-tel-input/17.0.21/js/utils.js',
      initialCountry: 'auto',
      geoIpLookup: (callback) => {
        fetch('https://ipapi.co/json')
          .then((res) => res.json())
          .then((data: { country_code: string }) => callback(data.country_code))
          .catch(() => callback('pl'));
      },
      separateDialCode: true,
    });
  }

  (['passengers', 'bags', 'seats'] as const).forEach((id) => {
    const input = inputById(id);
    if (!input) return;
    input.addEventListener('input', () => {
      input.value = input.value.replace(/\D/g, '');
      const max = parseInt(input.max, 10) || 9999;
      if (parseInt(input.value, 10) > max) input.value = String(max);
      updateStepperButtons();
      validateForm();
    });
    input.addEventListener('blur', () => {
      const min = parseInt(input.min, 10) || 0;
      if (!input.value || parseInt(input.value, 10) < min) input.value = String(min);
      updateStepperButtons();
      validateForm();
    });
  });

  // --- MULTI-TRIP LISTENERY ---
  byId('openMultiModalBtn')?.addEventListener('click', openMultiModal);
  byId('closeMultiModal')?.addEventListener('click', closeMultiModal);
  byId('addAnotherTripBtn')?.addEventListener('click', addMultiTrip);
  byId('confirmMultiTripsBtn')?.addEventListener('click', closeMultiModal);

  // Summary card → otwarcie modala w odpowiednim trybie
  byId('viewItineraryBtn')?.addEventListener('click', openMultiModalAsView);
  byId('editMultiTripsBtn')?.addEventListener('click', openMultiModal);

  // Modal w trybie View → przejście do Edit / Confirm
  byId('switchToEditBtn')?.addEventListener('click', showEditView);
  byId('confirmReadonlyBtn')?.addEventListener('click', closeMultiModal);

  // Inicjalizacja: wypełnij do min. 3 podróży, jeśli sesja jest pusta
  initMultiTrips();

  form.addEventListener('submit', (e) => void handleSubmit(e));

  document.addEventListener('click', (e) => {
    const btn = (e.target as HTMLElement).closest<HTMLButtonElement>('button[data-step]');
    if (btn) {
      e.preventDefault();
      changeStep(btn.dataset.step as StepperName, Number(btn.dataset.dir));
    }
  });

  form.addEventListener('input', validateForm);
  form.addEventListener('change', validateForm);
  byId('oneWayBtn')?.addEventListener('click', () => handleTabSwitch('oneway'));
  byId('returnBtn')?.addEventListener('click', () => handleTabSwitch('return'));
  byId('multiBtn')?.addEventListener('click', () => handleTabSwitch('multitrip'));

  setTripType('return');
  updateStepperButtons();
  updateSummaryView();
  validateForm();
}

// --- BOOTSTRAP ---
// Na stronie (Bricks) wystarczy <div id="volubus-form-root"></div> + ten skrypt.
// Markup formularza jest wbudowany w bundle i wstrzykiwany do kontenera.

const ROOT_ID = 'volubus-form-root';

function bootstrap(): void {
  const root = document.getElementById(ROOT_ID);
  if (!root) {
    console.warn(`[volubus-form] Nie znaleziono kontenera #${ROOT_ID} — formularz nie został zamontowany.`);
    return;
  }
  root.innerHTML = formHtml;
  initForm();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', bootstrap);
} else {
  bootstrap();
}
